import { useEffect, useMemo, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { CoachThreadMessageSchema, type CoachCard, type CoachOpening, type CoachThreadMessage, type FocusView, type PracticeCurrentResponse, type PracticeView } from "@pushback-and-chaos/contracts";
import { HomeScreen, type ArrangeStyle, type OnboardingState } from "../screens/home.js";
import type { LibraryCase } from "../screens/library.js";
import pageRead from "./page-read-fixtures.generated.json";

/**
 * THE FRONT DOOR REVIEW BENCH (front door in the Storyteller's vocabulary,
 * 2026-09-20). Dev and static preview only; a production build tree-shakes
 * the whole module away, as it does the play bench beside it.
 *
 * It drives the REAL front door — the real panels, the real cards, the real
 * composer — against fixture content chosen to test the composition rather
 * than the server: a full hand of seven cards, a conversation long enough to
 * scroll, and the onboarding thread a new learner meets.
 *
 *   #/front-door-review              the standing front door
 *   #/front-door-review?cards=2      a thin hand
 *   #/front-door-review?state=new    a learner with no profile yet
 *   #/front-door-review?state=waiting   the opening still arriving
 *   #/front-door-review?state=writing   the coach composing a reply, so the
 *                                       portrait is flipping through poses
 *   #/front-door-review?state=typing    the opening arriving, and typing
 *   #/front-door-review?turns=6         a conversation long enough to page
 *   #/front-door-review?thread=days     five days of one learner's conversation
 *                                       with a welcome before the first: the
 *                                       coach-gate replay's own words
 *                                       (scripts/page-read-fixtures.mjs), for
 *                                       the page-length read (Andre's ruling
 *                                       15, 2026-10-27); a new day begins a
 *                                       page, and day one, a long sitting,
 *                                       also turns one by the page budget;
 *                                       `turns` is ignored
 *   #/front-door-review?thread=days&days=3   only the first three of those days
 *   #/front-door-review?desk=papers     the desk's own papers too — a chosen
 *                                       focus, the practice paper with one
 *                                       offer, and the email card — so the
 *                                       arrange controls (2026-10-31) can be
 *                                       tried on a full desk
 *   #/front-door-review?arrange=rows    "tidy." in its first form, readable
 *                                       rows, for the comparison; without it
 *                                       the tidy piles the papers by kind
 *                                       (Andre's second pass, 2026-10-31)
 *   #/front-door-review?links=on        two pushed cards that remember the
 *                                       reply that made them: Drayvale for
 *                                       the chosen focus, Brindlemere for an
 *                                       earlier one, so the markers, the drawn
 *                                       connection and "my focus." under
 *                                       arrange by. can be reviewed (steps C
 *                                       and D of the focus-links work,
 *                                       2026-10-31); best with desk=papers
 *
 * THE ARRANGE CONTROLS are on here always (`arrange`): the bench is where
 * "tidy." is reviewed before production is allowed to show it.
 *
 * Nothing here is learner state and none of it reaches a server.
 */

const CASES: readonly LibraryCase[] = [
  { slug: "harborlight", track: "CALIBRATION", client: "Harborlight Veterinary Group", primaryDimension: "AMBIGUITY_TOLERANCE", pressureContext: "REFINANCING", industry: "veterinary services", engagementQuestion: "Why has growth stalled?", tension: "Sixty-eight clinics. Eight days. One board pre-read." },
  { slug: "wrenhall", track: "FRAMEWORKS", client: "Wrenhall Software", primaryDimension: "STRUCTURE", pressureContext: "LITIGATION", industry: "software", engagementQuestion: "What does growth mean here?", tension: "Everyone agrees the growth stopped." },
  { slug: "cawthorne", track: "CONFLICT_MANAGEMENT", client: "Cawthorne Forge", primaryDimension: "COMMUNICATION", pressureContext: "NEW_LEADER", industry: "metal forming", engagementQuestion: "Whose plan is this?", tension: "The new chief executive has already said what the answer is." },
  { slug: "brindlemere", track: "CREATIVITY", client: "Brindlemere Mills", primaryDimension: "JUDGMENT", pressureContext: "CUSTOMER_ULTIMATUM", industry: "textiles", engagementQuestion: "What is left to sell?", tension: "The obvious route was closed before you arrived." },
  { slug: "drayvale", track: "CHAOS", client: "Drayvale Transit", primaryDimension: "QUANTITATIVE", pressureContext: "REGULATOR", industry: "public transport", engagementQuestion: "Which number is true?", tension: "Three systems, three answers, one hearing." },
];

const WELCOME: readonly string[] = [
  "Your judgment is running ahead of the rest of your record, and it has been for three engagements now: you name the trade you are making before anybody asks you to defend it, and you say what it costs.",
  "What moved since Cawthorne is your communication. The recommendation held its shape under the second and third questions, which it did not do in March.",
  "Quantitative is the one to work on next. Take it into Harborlight and build the bridge yourself before you look at anybody else's — then say out loud what would have to be true for it to be wrong.",
];

const CARDS: readonly CoachCard[] = [
  { kind: "PUSHED", caseSlug: "drayvale", sessionId: null, title: "Drayvale Transit", reason: "You asked for something that makes you do the arithmetic under pressure. This is that." },
  { kind: "NEXT", caseSlug: "harborlight", sessionId: null, title: "Harborlight Veterinary Group", reason: "Trains quantitative, the one the record says is furthest behind." },
  { kind: "CONTINUE", caseSlug: "wrenhall", sessionId: "s1", title: "Wrenhall Software", reason: "You have this engagement open; it stopped on the second day." },
  { kind: "SAVED", caseSlug: "cawthorne", sessionId: null, title: "Cawthorne Forge", reason: "You put this one on the shelf a fortnight ago." },
  { kind: "SHELF", caseSlug: "brindlemere", sessionId: null, title: "Brindlemere Mills", reason: "The obvious route is closed here, which is where your judgment reads best." },
  { kind: "PLAN", caseSlug: null, sessionId: null, title: "Your development plan.", reason: "It changed after Cawthorne. Read it once." },
];

const THREAD: readonly CoachThreadMessage[] = [
  { id: "m1", from: "you", text: "Which of those should I do first, and why that one rather than the plan?", at: "2026-09-19T09:01:00.000Z" },
  { id: "m2", from: "coach", text: "Harborlight, and the plan after it rather than before. The plan is a reading of what has already happened; it will say the same thing in an hour. The case is the only thing that changes the reading.", at: "2026-09-19T09:01:10.000Z" },
  { id: "m3", from: "you", text: "I keep reaching for the same decomposition in the first turn. Is that the thing to break?", at: "2026-09-19T09:02:00.000Z" },
  { id: "m4", from: "coach", text: "It is, and not by learning another one. In the first turn of Harborlight, write the decomposition out of the client's own economics — what they sell, to whom, and what it costs them to serve — before you write anything that has a name. If what comes out looks like the one you always reach for, keep it, and now you know it was chosen.", at: "2026-09-19T09:02:20.000Z" },
];

/**
 * `?links=on`: the same hand, with two pushes remembering the reply that made
 * them (DESIGN-PASS-2026-10-31-FOCUS-LINKS). Brindlemere was pushed under an
 * earlier focus and keeps that focus's id; Drayvale under the one the bench's
 * desk shows as chosen. The thread gains the two replies the cards point at.
 */
const FOCUS_ID = "bench-focus";
const LINKED_CARDS: readonly CoachCard[] = CARDS.map((card) => {
  if (card.caseSlug === "drayvale") return { ...card, messageId: "m8", focusId: FOCUS_ID };
  if (card.caseSlug === "brindlemere") return { kind: "PUSHED", caseSlug: card.caseSlug, sessionId: null, title: card.title, reason: "The obvious route is closed here, which is where your judgment reads best.", messageId: "m6", focusId: "an-earlier-focus" };
  return card;
});
const LINKED_THREAD: readonly CoachThreadMessage[] = [
  ...THREAD,
  { id: "m5", from: "you", text: "Give me one for the focus, then.", at: "2026-09-19T09:03:00.000Z" },
  { id: "m6", from: "coach", text: "Brindlemere Mills. The obvious route was closed before you arrived, so what is left is judgment about what is still worth selling, which is the focus you chose.\n\nRead the brief twice before the first turn.", at: "2026-09-19T09:03:10.000Z" },
  { id: "m7", from: "you", text: "I have changed the focus to the arithmetic. Which one now?", at: "2026-09-19T09:04:00.000Z" },
  { id: "m8", from: "coach", text: "Then Drayvale Transit. Three systems give three numbers and the hearing wants one; you will do the arithmetic in the room with the regulator waiting, which is the focus exactly.\n\nOpen it when you have an hour.", at: "2026-09-19T09:04:10.000Z" },
];

const INTAKE: OnboardingState = { stage: "intake", exchanges: [], question: null, draft: null, evidence: [], rehearsal: null };

/** The desk's own papers for `?desk=papers`: a chosen focus, one offered exercise beside the generic practice paper, and the email card. */
const FOCUS: FocusView = {
  focusId: FOCUS_ID,
  state: { dimension: "CALIBRATION", text: "Say what would change my mind before I say what I think.", origin: "LEARNER_CHOSEN", brief_id: null, status: "CHOSEN", chosen_statement: "I want to work on calibration.", attempts: [], self_reports: [], reviews: [], replaced_by: null, last_seq: 2 },
  standing: { chosen: true, tried: false, selfReported: false, shownInEngagement: false, acrossSituations: false },
};
const OFFER: PracticeView = {
  exerciseId: "bench-exercise",
  brief: {
    id: "two-explanations", version: 1, dimension: "CALIBRATION", title: "Two explanations and the thing that would decide",
    target_behaviour: "State two plausible explanations, then name an observation that would distinguish them.",
    why: "A recommendation that carries its own rival is harder to argue with.", situation: "A clinic group's margin has slipped.", task: "Write the two explanations and the observation.",
    criteria: [{ id: "first-explanation", text: "A first explanation of the slip" }, { id: "second-explanation", text: "A second, different explanation" }, { id: "distinguisher", text: "An observation that would tell the two apart" }],
    acceptable_variations: [], hint: "Name the second cause first.", example: "It could be wages, or the mix.", finishing: "Both explanations and the observation are on the page.",
    variations: [], counterpart: null,
  },
  state: { brief_id: "two-explanations", brief_version: 1, dimension: "CALIBRATION", origin: "LEARNER_ASKED", stage: "OFFERED", stage_before_pause: null, attempts: [], criteria: { "first-explanation": "UNREAD", "second-explanation": "UNREAD", distinguisher: "UNREAD" }, hint_shown: false, example_shown: false, revision: null, variation_id: null, concluded_by: null, finished: false, last_seq: 1 },
  reflection: [],
  can: { accept: true, decline: true, attempt: false, hint: false, example: false, revision: false, variation: false, pause: false, resume: false, conclude: false },
};
const PRACTICE: PracticeCurrentResponse = { offers: [OFFER], current: null, available: [{ briefId: "two-explanations", title: OFFER.brief.title, dimension: "CALIBRATION", targetBehaviour: OFFER.brief.target_behaviour }] };

/** The welcome and its time: the bench's own, or the page-read fixture's, which is written before that thread's first day. */
interface Welcome {
  readonly paragraphs: readonly string[];
  readonly generatedAt: string;
}
const BENCH_WELCOME: Welcome = { paragraphs: WELCOME, generatedAt: "2026-09-19T09:00:00.000Z" };

function opening(cards: number, welcome: Welcome = BENCH_WELCOME, linked = false): CoachOpening {
  const hand = linked ? LINKED_CARDS : CARDS;
  return { welcome: [...welcome.paragraphs], cards: hand.slice(0, Math.max(1, Math.min(cards, hand.length))), source: "model", generatedAt: welcome.generatedAt };
}

function noop(): void {
  /* the bench changes nothing */
}

/** A conversation long enough to need pages, for the page control and the portrait on every page. */
function longThread(turns: number): readonly CoachThreadMessage[] {
  const lines: CoachThreadMessage[] = [...THREAD];
  for (let turn = 1; turn <= turns; turn += 1) {
    lines.push({ id: `q${String(turn)}`, from: "you", text: "And what would you have me do differently in the first hour of it?", at: "2026-09-19T10:00:00.000Z" });
    lines.push({
      id: `a${String(turn)}`,
      from: "coach",
      text: "Write what you think is going on before you ask anybody for anything, and say what would have to be true for you to be wrong. The people in the room answer the thinking, not the request — that is the whole difference between a document arriving and a lead opening.",
      at: "2026-09-19T10:00:10.000Z",
    });
  }
  return lines;
}

/**
 * Five days of one learner's conversation with the coach (page-read-fixtures.generated.json):
 * each line keeps its own day, so the page breaks the front door makes at a new
 * day, and by the budget inside day one's long sitting, are the ones it makes
 * for a learner. `days` keeps the first N of them.
 *
 * The fixture is read only inside these two functions, never at the top of
 * the module, so a production build, which drops this bench, drops the
 * fixture with it.
 */
function daysThread(days: number): readonly CoachThreadMessage[] {
  const kept = Number.isInteger(days) && days >= 1 ? pageRead.coach.days.slice(0, days) : pageRead.coach.days;
  return CoachThreadMessageSchema.array().parse(kept.flatMap((day) => day.lines));
}
/** The welcome written before that thread's first day. */
function daysWelcome(): Welcome {
  return { paragraphs: pageRead.coach.welcome, generatedAt: pageRead.coach.generatedAt };
}

export function FrontDoorReviewBench(): ReactNode {
  const params = new URLSearchParams(globalThis.location.hash.split("?")[1] ?? "");
  const cards = Number.parseInt(params.get("cards") ?? "6", 10);
  const turns = Number.parseInt(params.get("turns") ?? "0", 10);
  const state = params.get("state") ?? "standing";
  const byDay = params.get("thread") === "days";
  const days = Number.parseInt(params.get("days") ?? "", 10);
  const papers = params.get("desk") === "papers";
  const arrange: ArrangeStyle = params.get("arrange") === "rows" ? "rows" : "stacks";
  const linked = params.get("links") === "on";
  const client = useMemo(() => new QueryClient({ defaultOptions: { queries: { retry: false, staleTime: Number.POSITIVE_INFINITY } } }), []);
  const thread = useMemo(
    () => (byDay ? daysThread(days) : Number.isFinite(turns) && turns > 0 ? longThread(turns) : linked ? LINKED_THREAD : THREAD),
    [byDay, days, turns, linked],
  );

  /*
   * `?state=typing` holds the opening back for a moment and then delivers it,
   * which is the only way to watch the typewriter: words already on the page
   * when it mounts are never replayed.
   */
  const [arrived, setArrived] = useState(state !== "typing");
  useEffect(() => {
    if (arrived) return;
    const wait = setTimeout(() => { setArrived(true); }, 900);
    return () => { clearTimeout(wait); };
  }, [arrived]);
  const speaking = state === "standing" || state === "writing" || (state === "typing" && arrived);

  return (
    <QueryClientProvider client={client}>
      <HomeScreen
        opening={speaking ? opening(Number.isFinite(cards) ? cards : 6, byDay ? daysWelcome() : BENCH_WELCOME, linked) : null}
        openingFailed={false}
        firstVisit={state === "new"}
        gated={false}
        onboarding={state === "new" ? INTAKE : null}
        onOnboard={noop}
        onAnswerLevel={noop}
        onConfirmProfile={noop}
        onRedoProfile={noop}
        onRehearse={noop}
        onSkipRehearsal={noop}
        onBeginAfterRehearsal={noop}
        thread={speaking ? thread : []}
        pendingText={state === "writing" ? "So what do I actually write in the first turn?" : null}
        busy={state === "writing"}
        error={null}
        cases={CASES}
        onSend={noop}
        onOpenSettings={noop}
        dictate={null}
        libraryState={null}
        onContinue={noop}
        onStart={noop}
        onReadPrologue={noop}
        onSetSaved={noop}
        onReadPlan={noop}
        focus={papers ? { current: FOCUS } : null}
        practice={papers ? PRACTICE : null}
        emailChoices={papers ? { signedIn: false } : null}
        deskSettled={speaking}
        arrange={arrange}
      />
    </QueryClientProvider>
  );
}
